import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from "../DropDown";
import DatePicker from "../DatePicker";

interface InputComponentProps {
  value: string;
  onError: (error: string) => void;
  onChange: (value: string, isValid: boolean) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  placeholder: string;
  errorText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  validators?: Array<(text: string) => { isValid: boolean; error: string }>;
  onSubmit?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  bgClass?: string;
  containerClassName?: string;
  type?: string;
  datesForPicker?: Date[];
  height?: string;
  isErrored?: boolean;
  datePickerProps?: any;
  dropdownProps: any;
}

const InputComponent: React.FC<InputComponentProps> = ({
  value,
  onChange,
  onFocus,
  placeholder,
  leftIcon,
  rightIcon,
  validators = [],
  onError = () => {},
  onSubmit = () => {},
  bgClass,
  height,
  containerClassName = "min-h-12",
  type = 'text',
  datesForPicker = [],
  isErrored = false,
  datePickerProps,
  dropdownProps = {},
}) => {
  const hasLeftIcon = Boolean(leftIcon);
  const hasRightIcon = Boolean(rightIcon);

  const { t, i18n } = useTranslation();

  const paddingLeftRight = hasLeftIcon ? '36px' : '4px';
  const [parsedDate, setParsedDate] = useState<Date | null>(value && value.length > 10 ? new Date(value) : null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [error, setError] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const bg = bgClass || 'bg-indigo-1000';
  const inputClasses = `
    h-full py-3 block w-full ${bg} text-white rounded-md
    border-none 
    ${!isErrored ? "ring-1 ring-indigo-700 focus:outline-none focus:ring-2 focus:ring-primary-500" : "ring-2 ring-cherry-500 focus:outline-none focus:ring-2 focus:ring-cherry-500"}
    resize-none overflow-hidden
  `;

  useEffect(() => {
    if (value && value.length > 10) {
      setParsedDate(new Date(value))
    }
  }, [value])

  const runValidators = (newValue: string) => {
    let isValidOverall = true;
    let errorMessage = '';

    validators.forEach((validator) => {
      const { isValid, error } = validator(newValue);
      if (!isValid) {
        isValidOverall = false;
        errorMessage = error;
      }
    });

    setError(errorMessage);
    onChange(newValue, isValidOverall);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit(e);
    }
  };

  const handleDatePick = (date: Date | null) => {
    if (date) {
      const isoString = date.toISOString();
      runValidators(isoString);
      setParsedDate(date);
      setDatePickerOpen(false); // Close the dropdown only when a date is selected
    } else {
      runValidators('');
      setParsedDate(null);
    }
  };

  const dateToFormattedString = (date: Date) => {
    if (!date) return '';
    return date.toLocaleDateString(i18n.language === 'sv' ? 'sv-SE' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (type === 'date') {
    if (!parsedDate && value.length > 10) {
      const _date = new Date(value);
      setParsedDate(_date);
    }
    return (
      <div className={`${containerClassName}`}>
        <div className={`relative ${height}`}>
          <Dropdown
            label={parsedDate ? dateToFormattedString(parsedDate) : placeholder}
            className="w-full"
            isOpen={datePickerOpen}
            setIsOpen={setDatePickerOpen}
            onOpen={() => setDatePickerOpen(true)}
            onClose={() => setDatePickerOpen(false)}
            borderColor={`${datePickerOpen ? 'border-primary-500' : 'border-indigo-700'}`}
            menuClassName="bg-indigo-900"
            showIcon={false}
            hasBorder={false}
            {...dropdownProps}
          >
            <DatePicker
              dates={datesForPicker}
              clickDate={handleDatePick}
              preSelectedDate={parsedDate}
              {...datePickerProps}
            />
          </Dropdown>

          {hasLeftIcon && (
            <div className="absolute flex items-center pointer-events-none" style={{insetBlock: 0, paddingLeft: 16}}>
              {leftIcon}
            </div>
          )}
          {hasRightIcon && (
            <div className="absolute right-0 flex items-center pr-4 pointer-events-none"
                 style={{insetBlock: 0}}>
              {rightIcon}
            </div>
          )}
        </div>

        {error && <p className="text-cherry-500">{error}</p>}
      </div>
    );
  }

  return (
    <div className={`px-0.5 z-0 ${containerClassName}`}>
      <div className={`relative ${height}`}>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => {
            const newValue = e.target.value;
            runValidators(newValue);
          }}
          onFocus={onFocus}
          onKeyDown={handleKeyPress}
          placeholder={placeholder}
          className={`${inputClasses} box-border border border-gray-300 focus:ring-2 focus:ring-blue-500`}
          style={{
            whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word',
            minHeight: '2.5rem',
            lineHeight: '1.5',
            paddingLeft: paddingLeftRight,
            paddingRight: paddingLeftRight,
          }}
          rows={1}
        />

        {hasLeftIcon && (
          <div className="absolute left-0 flex items-center pl-4 z-40" style={{insetBlock: 0, paddingLeft: 10}}>
            {leftIcon}
          </div>
        )}
        {hasRightIcon && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-4 z-40">
            {rightIcon}
          </div>
        )}
      </div>

      {error && <p className="text-cherry-500">{error}</p>}
    </div>
  );
};

export default InputComponent;